import * as React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { forDesktop, isDesktop } from '@westwing/ui-kit/breakPoints';
import HorizontalScrollableElement from '../ScrollableElement';
import StateInterface from '../../redux/types';
import { ProductInterface, WishlistTrackingFn } from '../../redux/products/types';
import useGa from '../../hooks/useGa';
import useBellaTranslator from '../../hooks/useTranslator';
import ProducSlider from '../Generic/Sliders/ProductSlider';
import GenericImpressionWrapper from '../Generic/GenericImpressionWrapper';
import useHomepageTracking from './useHomepageTracking';
import useConfig from 'Client/hooks/useConfig';
import useRouter from 'Client/hooks/useRouter';

const BestSellerProductsSliderWrapper = styled(HorizontalScrollableElement)`
    color: ${({ theme }) => theme.colors.charcoal100};
    padding-top: 20px;
    ${forDesktop`
        padding-top: 23px;
    `}
`;

const BestSellerProductsSlider = () => {
    const t = useBellaTranslator();
    const gaTracking = useGa();
    const { trackBestSellerSliderImpression } = useHomepageTracking();
    const { currency } = useConfig();
    const {
        host,
        location: { pathname },
    } = useRouter();

    const absoluteUrl = `${host}${pathname}`;

    const bestSellersProducts = useSelector<StateInterface, ProductInterface[]>(
        state => state.bestSellerProducts.products
    );

    const clickTrackingFn = React.useCallback(() => {
        gaTracking.homepage.trackBestSellerProductSliderClick();
    }, [gaTracking]);

    const wishListTrackingFn: WishlistTrackingFn = React.useCallback(
        ({ action, sku, category, name, brand, price }) => {
            gaTracking.trackWishListHeartClick({
                action,
                label: `Bestseller_slider,${sku}`,
                sku,
                category,
                name,
                brand,
                price,
                currency,
                url: absoluteUrl,
            });
        },
        [gaTracking, currency, absoluteUrl]
    );

    const bestSellerProductsWithTracking = bestSellersProducts.map(product => ({
        ...product,
        clickTrackingFn,
        wishListTrackingFn,
    }));

    const sliderTrackLabel = 'Best_sellers';
    const trackArrowClicks = () => {
        if (isDesktop()) {
            gaTracking.homepage.trackSliderArrowComponentClick(sliderTrackLabel);
        }
    };

    const impressionAction = React.useCallback(() => {
        trackBestSellerSliderImpression();
        gaTracking.homepage.trackSliderImpression('Best sellers slider impression');
    }, [gaTracking.homepage, trackBestSellerSliderImpression]);

    return (
        <GenericImpressionWrapper action={impressionAction}>
            <BestSellerProductsSliderWrapper
                title={t('Bestsellers')}
                arrowClickTrackFunction={trackArrowClicks}
                showNavArrows
                showHeader
            >
                <ProducSlider products={bestSellerProductsWithTracking} />
            </BestSellerProductsSliderWrapper>
        </GenericImpressionWrapper>
    );
};
export default BestSellerProductsSlider;
